import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  LinearProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Collapse,
  IconButton,
  Autocomplete,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { detectResponseType, getRatingColor } from '../utils/sampleResponses';
import AuthService from '../services/AuthService';
import ChartRenderer from './ChartRenderer';
import TimelineRenderer from './TimelineRenderer';
import DiagnosticRenderer from './DiagnosticRenderer';
import FileSearchResults from './FileSearchResults';

// Atom writes Markdown tables (| a | b |); without GFM they showed as raw pipes in the chat
const MARKDOWN_PLUGINS = [remarkGfm];

// Render action buttons for STRUCTURED responses
const ActionButtons = ({ actions, onActionClick, colors }) => {
  if (!actions || actions.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
      {actions.map((action, index) => (
        <Button
          key={index}
          variant="outlined"
          size="small"
          onClick={() => onActionClick(action.sentence, action)}
          sx={{
            borderColor: '#22c55e',
            color: '#22c55e',
            '&:hover': {
              borderColor: '#16a34a',
              bgcolor: 'rgba(34, 197, 94, 0.08)',
            },
          }}
        >
          {action.buttonText}
        </Button>
      ))}
    </Box>
  );
};

// Render rating badge
const RatingBadge = ({ rating }) => {
  const color = getRatingColor(rating);
  return (
    <Chip
      label={rating}
      size="small"
      sx={{
        bgcolor: `${color}20`,
        color: color,
        fontWeight: 600,
        borderRadius: 0,
        border: `1px solid ${color}`,
      }}
    />
  );
};

// Render wizard progress bar
const WizardProgress = ({ progress, colors }) => {
  if (!progress) return null;

  const percentage = (progress.stepNumber / progress.totalSteps) * 100;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
          Step {progress.stepNumber} of {progress.totalSteps}: {progress.phaseLabel}
        </Typography>
        <Typography variant="caption" sx={{ color: '#22c55e' }}>
          {Math.round(percentage)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 4,
          borderRadius: 0,
          bgcolor: colors.surfaceElevated,
          '& .MuiLinearProgress-bar': {
            bgcolor: '#22c55e',
            borderRadius: 0,
          },
        }}
      />
    </Box>
  );
};

// Render system assumptions strip with inline editing
const AssumptionsStrip = ({ assumptions, colors, onChangeAssumption }) => {
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editValue, setEditValue] = useState('');

  if (!assumptions || assumptions.length === 0) return null;

  const handleEditClick = (assumption) => {
    setEditingFieldId(assumption.fieldId);
    setEditValue(String(assumption.assumedValue || ''));
  };

  const handleSave = (fieldId) => {
    if (onChangeAssumption) {
      onChangeAssumption(fieldId, editValue);
    }
    setEditingFieldId(null);
    setEditValue('');
  };

  const handleCancel = () => {
    setEditingFieldId(null);
    setEditValue('');
  };

  const handleKeyDown = (e, fieldId) => {
    if (e.key === 'Enter') {
      handleSave(fieldId);
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        bgcolor: 'rgba(59, 130, 246, 0.08)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderLeft: '4px solid #3b82f6',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
        <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, textTransform: 'uppercase' }}>
          We assumed
        </Typography>
      </Box>
      {assumptions.map((assumption) => (
        <Box key={assumption.fieldId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
          {editingFieldId === assumption.fieldId ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                {assumption.label}:
              </Typography>
              <TextField
                size="small"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, assumption.fieldId)}
                autoFocus
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    bgcolor: colors.background,
                    '& fieldset': { borderColor: '#3b82f6' },
                    '&:hover fieldset': { borderColor: '#3b82f6' },
                    '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                  },
                  '& .MuiInputBase-input': {
                    color: colors.textPrimary,
                    fontSize: '0.875rem',
                    py: 0.5,
                  },
                }}
              />
              <Button
                size="small"
                onClick={() => handleSave(assumption.fieldId)}
                sx={{ minWidth: 'auto', color: '#22c55e', fontSize: '0.75rem' }}
              >
                Save
              </Button>
              <Button
                size="small"
                onClick={handleCancel}
                sx={{ minWidth: 'auto', color: colors.textMuted, fontSize: '0.75rem' }}
              >
                Cancel
              </Button>
            </Box>
          ) : (
            <>
              <Box>
                <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                  <strong>{assumption.label}:</strong> {String(assumption.assumedValue)}
                </Typography>
                {assumption.rationale && (
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    {assumption.rationale}
                  </Typography>
                )}
              </Box>
              {assumption.editable && (
                <IconButton
                  size="small"
                  onClick={() => handleEditClick(assumption)}
                  sx={{ color: '#3b82f6' }}
                  title="Edit this value"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
        </Box>
      ))}
    </Box>
  );
};

// Render confidence copy
const ConfidenceCopy = ({ message, colors }) => {
  if (!message) return null;

  return (
    <Typography variant="caption" sx={{ color: colors.textMuted, fontStyle: 'italic', mb: 1, display: 'block' }}>
      {message}
    </Typography>
  );
};

// Render post-wizard next actions
const PostWizardActions = ({ nextActions, colors, onActionClick }) => {
  console.log('PostWizardActions rendering with:', nextActions);
  if (!nextActions || nextActions.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderLeft: '4px solid #22c55e',
      }}
    >
      <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1.5 }}>
        What would you like to do next?
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {nextActions.map((action) => (
          <Button
            key={action.actionId}
            variant={action.primary ? 'contained' : 'outlined'}
            size="small"
            onClick={() => {
              console.log('PostWizardAction clicked:', action.label, 'triggerPhrase:', action.triggerPhrase);
              onActionClick && onActionClick(action.triggerPhrase);
            }}
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              ...(action.primary && {
                bgcolor: '#22c55e',
                color: '#09090b',
                '&:hover': { bgcolor: '#16a34a' },
              }),
              ...(!action.primary && {
                borderColor: colors.border,
                color: colors.textSecondary,
              }),
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: action.primary ? 600 : 400 }}>
                {action.label}
              </Typography>
              {action.description && (
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
                  {action.description}
                </Typography>
              )}
            </Box>
          </Button>
        ))}
      </Box>
    </Box>
  );
};

// Render wizard form field
const WizardField = ({ field, colors, onChange, onDefer, onBlur, onScriptValidate, currentValue, disabled = false, collectedData = {}, validationError = null, isValidating = false }) => {
  // Show error if backend validation failed OR frontend validation error exists
  const hasError = field.status === 'VALIDATION_FAILED' || !!validationError;
  const errorMessage = validationError || field.error?.message;
  const isDeferred = field.status === 'DEFERRED';
  const value = currentValue !== undefined ? currentValue : (field.value || field.defaultValue || '');

  // Show deferred state with gray styling and clock icon
  if (isDeferred) {
    return (
      <Box sx={{ mb: 2, p: 1, bgcolor: 'rgba(156, 163, 175, 0.1)', border: '1px solid #9ca3af', borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
            {field.label}: <em>Decide later</em>
          </Typography>
        </Box>
        {field.deferralHint && (
          <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
            {field.deferralHint}
          </Typography>
        )}
      </Box>
    );
  }

  const commonSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      bgcolor: colors.background,
      '& fieldset': {
        borderColor: hasError ? '#ef4444' : value ? '#22c55e' : colors.borderLight,
      },
      '&:hover fieldset': {
        borderColor: '#22c55e',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#22c55e',
      },
    },
    '& .MuiInputBase-input': {
      color: colors.textPrimary,
    },
    '& .MuiInputLabel-root': {
      color: colors.textSecondary,
    },
  };

  switch (field.type) {
    case 'COLOR_PALETTE':
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
            {field.label}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {field.options?.map((opt) => (
              <Box
                key={opt.value}
                onClick={() => !disabled && onChange(field.fieldId, opt.value)}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: opt.value,
                  cursor: disabled ? 'default' : 'pointer',
                  borderRadius: 0,
                  border: value === opt.value
                    ? '3px solid white'
                    : '1px solid transparent',
                  boxShadow: value === opt.value
                    ? `0 0 0 2px ${opt.value}`
                    : 'none',
                  opacity: disabled ? 0.5 : 1,
                  transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                  '&:hover': {
                    transform: disabled ? 'none' : 'scale(1.1)',
                  },
                }}
                title={opt.label}
              />
            ))}
          </Box>
          {field.helpText && (
            <FormHelperText sx={{ color: colors.textMuted }}>{field.helpText}</FormHelperText>
          )}
          {errorMessage && (
            <FormHelperText sx={{ color: '#ef4444' }}>{errorMessage}</FormHelperText>
          )}
        </Box>
      );

    case 'CHECKBOX': {
      const isChecked = value === true || value === 'true';
      return (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                disabled={disabled}
                onChange={(e) => onChange(field.fieldId, e.target.checked ? 'true' : 'false')}
                sx={{
                  color: hasError ? '#ef4444' : colors.borderLight,
                  '&.Mui-checked': { color: '#22c55e' },
                }}
              />
            }
            label={<Typography variant="body2" sx={{ color: colors.textPrimary }}>{field.label}</Typography>}
          />
          {field.helpText && (
            <FormHelperText sx={{ color: colors.textMuted, ml: 4 }}>{field.helpText}</FormHelperText>
          )}
          {errorMessage && (
            <FormHelperText sx={{ color: '#ef4444', ml: 4 }}>{errorMessage}</FormHelperText>
          )}
        </Box>
      );
    }

    case 'DROPDOWN':
      // Check for options from context (e.g., available_lpns pulled from previous wizard)
      // The target field name is used as the key (first field in pull_from_context list)
      const contextOptionsKey = `available_${field.fieldId}s`;
      const headerContext = collectedData?.receipt_header || {};

      // Try multiple locations for context options:
      // 1. receipt_header.available_{fieldId}s (primary location)
      // 2. Direct in collectedData (fallback)
      let contextOptions = headerContext[contextOptionsKey];
      if (!contextOptions && collectedData) {
        contextOptions = collectedData[contextOptionsKey];
      }

      // Debug logging to help troubleshoot
      if (process.env.NODE_ENV === 'development') {
        console.log(`DROPDOWN ${field.fieldId}: looking for ${contextOptionsKey}`, {
          headerContext,
          contextOptions,
          collectedData: collectedData ? Object.keys(collectedData) : null
        });
      }

      // Use context options if available, otherwise use field.options
      let dropdownOptions = field.options || [];
      if (contextOptions && Array.isArray(contextOptions) && contextOptions.length > 0) {
        dropdownOptions = contextOptions.map(opt =>
          typeof opt === 'object' ? { value: opt[field.fieldId] || opt.lpn || opt.value, label: opt[field.fieldId] || opt.lpn || opt.label || opt.value }
                                  : { value: opt, label: opt }
        );
      }

      return (
        <FormControl fullWidth size="small" error={hasError} disabled={disabled} sx={{ mb: 2 }}>
          <InputLabel sx={{ color: colors.textSecondary }}>{field.label}</InputLabel>
          <Select
            value={value}
            label={field.label}
            disabled={disabled}
            onChange={(e) => onChange(field.fieldId, e.target.value)}
            sx={{
              borderRadius: 0,
              bgcolor: colors.background,
              color: colors.textPrimary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hasError ? '#ef4444' : value ? '#22c55e' : colors.borderLight,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#22c55e',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#22c55e',
              },
            }}
          >
            {dropdownOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          {field.helpText && !errorMessage && (
            <FormHelperText sx={{ color: colors.textMuted }}>{field.helpText}</FormHelperText>
          )}
          {errorMessage && (
            <FormHelperText sx={{ color: '#ef4444' }}>{errorMessage}</FormHelperText>
          )}
        </FormControl>
      );

    case 'COMBOBOX':
      // COMBOBOX: Supports both dropdown selection and free text entry
      // Used for fields like LPN where user can select from context or enter manually
      const comboContextOptionsKey = `available_${field.fieldId}s`;
      const comboHeaderContext = collectedData?.receipt_header || {};

      // Try multiple locations for context options
      let comboContextOptions = comboHeaderContext[comboContextOptionsKey];
      if (!comboContextOptions && collectedData) {
        comboContextOptions = collectedData[comboContextOptionsKey];
        // Also check for direct field name in header context (e.g., lpn values pulled from context)
        if (!comboContextOptions && comboHeaderContext[field.fieldId]) {
          const headerValue = comboHeaderContext[field.fieldId];
          comboContextOptions = Array.isArray(headerValue) ? headerValue : [headerValue];
        }
      }

      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        console.log(`COMBOBOX ${field.fieldId}: looking for ${comboContextOptionsKey}`, {
          comboHeaderContext,
          comboContextOptions,
          collectedData: collectedData ? Object.keys(collectedData) : null,
          currentValue: value
        });
      }

      // Build options array from context or field.options
      let comboOptions = [];
      if (comboContextOptions && Array.isArray(comboContextOptions) && comboContextOptions.length > 0) {
        comboOptions = comboContextOptions.map(opt =>
          typeof opt === 'object'
            ? { value: opt[field.fieldId] || opt.lpn || opt.value || String(opt), label: opt[field.fieldId] || opt.lpn || opt.label || opt.value || String(opt) }
            : { value: String(opt), label: String(opt) }
        );
      } else if (field.options && Array.isArray(field.options)) {
        comboOptions = field.options.map(opt =>
          typeof opt === 'object'
            ? { value: opt.value, label: opt.label || opt.value }
            : { value: opt, label: opt }
        );
      }

      // Handle blur validation for COMBOBOX fields (e.g., LPN validation)
      // Pass entityName from field definition for backend to look up validation rules
      // Check if this field has any ON_BLUR validations configured
      const hasBlurValidation = field.validations?.some(v => v.trigger_on === 'ON_BLUR');
      const handleComboBlur = () => {
        if (onBlur && value && value.trim() && hasBlurValidation) {
          // Use entity_name from backend (snake_case in JSON), fall back to camelCase variants
          const entityName = field.entity_name || field.entity || field.entityName;
          const fieldName = field.field_name || field.fieldId;
          if (entityName) {
            onBlur(fieldName, value.trim(), entityName);
          } else {
            console.warn(`No entityName found for field ${field.fieldId}, skipping validation`);
          }
        }
      };

      return (
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            freeSolo
            options={comboOptions}
            getOptionLabel={(option) => {
              // Handle both string values and option objects
              if (typeof option === 'string') return option;
              return option.label || option.value || '';
            }}
            value={value || ''}
            disabled={disabled || isValidating}
            loading={isValidating}
            onChange={(event, newValue) => {
              // Handle selection from dropdown
              if (typeof newValue === 'string') {
                onChange(field.fieldId, newValue);
              } else if (newValue && newValue.value) {
                onChange(field.fieldId, newValue.value);
              } else {
                onChange(field.fieldId, '');
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              // Handle free text input
              if (reason === 'input') {
                onChange(field.fieldId, newInputValue);
              }
            }}
            onBlur={handleComboBlur}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                placeholder={field.placeholder || (comboOptions.length > 0 ? 'Select or type...' : 'Enter value...')}
                error={hasError}
                helperText={
                  isValidating
                    ? 'Validating...'
                    : (errorMessage || field.helpText || (comboOptions.length > 0 ? 'Select from list or enter a new value' : 'Enter the LPN to put away'))
                }
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isValidating ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    bgcolor: colors.background,
                    color: colors.textPrimary,
                    '& fieldset': {
                      borderColor: hasError ? '#ef4444' : value ? '#22c55e' : colors.borderLight,
                    },
                    '&:hover fieldset': {
                      borderColor: '#22c55e',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#22c55e',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.textSecondary,
                  },
                  '& .MuiFormHelperText-root': {
                    color: hasError ? '#ef4444' : colors.textMuted,
                  },
                }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-option': {
                bgcolor: colors.background,
                color: colors.textPrimary,
                '&:hover': {
                  bgcolor: colors.borderLight,
                },
                '&[aria-selected="true"]': {
                  bgcolor: '#22c55e20',
                },
              },
            }}
            ListboxProps={{
              sx: {
                bgcolor: colors.background,
                border: `1px solid ${colors.borderLight}`,
                '& .MuiAutocomplete-option': {
                  color: colors.textPrimary,
                },
              },
            }}
          />
          {comboOptions.length > 0 && (
            <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.5, display: 'block' }}>
              {comboOptions.length} option{comboOptions.length !== 1 ? 's' : ''} available from context
            </Typography>
          )}
        </Box>
      );

    case 'DATE':
      return (
        <TextField
          fullWidth
          size="small"
          type="date"
          label={field.label}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(field.fieldId, e.target.value)}
          error={hasError}
          helperText={errorMessage || field.helpText}
          InputLabelProps={{ shrink: true }}
          sx={{ ...commonSx, mb: 2 }}
        />
      );

    case 'NUMBER':
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={field.label}
          value={value}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(e) => onChange(field.fieldId, e.target.value)}
          error={hasError}
          helperText={errorMessage || field.helpText}
          inputProps={{ min: field.min }}
          sx={{ ...commonSx, mb: 2 }}
        />
      );

    case 'TEXTAREA':
      return (
        <TextField
          fullWidth
          size="small"
          multiline
          rows={3}
          label={field.label}
          value={value}
          placeholder={field.placeholder}
          disabled={disabled}
          onChange={(e) => onChange(field.fieldId, e.target.value)}
          error={hasError}
          helperText={errorMessage || field.helpText}
          sx={{ ...commonSx, mb: 2 }}
        />
      );

    case 'FILE':
      // FILE field type for document upload wizard
      const fileInputId = `file-input-${field.fieldId}`;
      const selectedFile = value; // value is the File object or null
      const acceptedTypes = field.acceptedTypes || '.pdf,.docx,.doc,.txt,.md';
      const maxSizeMb = field.maxSizeMb || 25;

      const handleFileSelect = (event) => {
        const file = event.target.files?.[0];
        if (file) {
          // Validate file size
          if (file.size > maxSizeMb * 1024 * 1024) {
            alert(`File size exceeds ${maxSizeMb}MB limit`);
            return;
          }
          onChange(field.fieldId, file);
        }
      };

      const handleRemoveFile = () => {
        onChange(field.fieldId, null);
      };

      const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
      };

      const handleDownloadTemplate = async () => {
        try {
          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';
          const response = await fetch(`${apiBaseUrl}${field.downloadTemplateUrl}`, {
            headers: { Authorization: `Bearer ${AuthService.getAccessToken()}` },
          });
          if (!response.ok) {
            alert('Could not download the template. Please try again.');
            return;
          }
          const disposition = response.headers.get('Content-Disposition') || '';
          const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
          const filename = filenameMatch ? filenameMatch[1] : 'template.csv';
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Error downloading template:', err);
          alert('Could not download the template. Please try again.');
        }
      };

      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
            {field.label}
          </Typography>

          {field.downloadTemplateUrl && (
            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ mb: 1.5, color: '#22c55e', textTransform: 'none' }}
            >
              Download Template
            </Button>
          )}

          {!selectedFile ? (
            // Drop zone / Upload button
            <Box
              sx={{
                border: `2px dashed ${hasError ? '#ef4444' : colors.borderLight}`,
                borderRadius: 0,
                p: 3,
                textAlign: 'center',
                bgcolor: colors.background,
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'border-color 0.2s, background-color 0.2s',
                '&:hover': disabled ? {} : {
                  borderColor: '#22c55e',
                  bgcolor: 'rgba(34, 197, 94, 0.05)',
                },
              }}
              onClick={() => !disabled && document.getElementById(fileInputId)?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) {
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    if (file.size > maxSizeMb * 1024 * 1024) {
                      alert(`File size exceeds ${maxSizeMb}MB limit`);
                      return;
                    }
                    onChange(field.fieldId, file);
                  }
                }
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
              <Typography variant="body2" sx={{ color: colors.textPrimary, mb: 0.5 }}>
                Click to upload or drag and drop
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {field.helpText || `Supported: PDF, Word, text files (max ${maxSizeMb}MB)`}
              </Typography>
            </Box>
          ) : (
            // Selected file display
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                bgcolor: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: 0,
              }}
            >
              <InsertDriveFileIcon sx={{ color: '#22c55e', fontSize: 32 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.textPrimary,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
              {!disabled && (
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}

          <input
            id={fileInputId}
            type="file"
            accept={acceptedTypes}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={disabled}
          />

          {errorMessage && (
            <FormHelperText sx={{ color: '#ef4444', mt: 0.5 }}>{errorMessage}</FormHelperText>
          )}
        </Box>
      );

    default: { // TEXT, AUTOCOMPLETE, etc.
      // Entity-backed validation (e.g. "does this ID already exist") via a
      // FLEX script -- reusable across any field/entity that sets
      // validation_script, not specific to Area. Distinct from the older
      // onBlur prop (COMBOBOX case above), which calls the separate
      // validation_rules/API_CALL mechanism.
      const handleTextBlur = () => {
        if (onScriptValidate && field.validationScript && value && String(value).trim()) {
          onScriptValidate(field.fieldId, String(value).trim(), field.validationScript);
        }
      };
      return (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label={field.label}
            value={value}
            placeholder={field.placeholder}
            disabled={disabled}
            onChange={(e) => onChange(field.fieldId, e.target.value)}
            onBlur={handleTextBlur}
            error={hasError}
            helperText={isValidating ? 'Checking…' : (errorMessage || field.helpText)}
            sx={commonSx}
          />
          {field.deferrable && !field.required && !disabled && (
            <Button
              size="small"
              onClick={() => onDefer && onDefer(field.fieldId)}
              sx={{
                mt: 0.5,
                fontSize: '0.75rem',
                color: '#9ca3af',
                '&:hover': { color: '#6b7280', bgcolor: 'transparent' },
              }}
              startIcon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            >
              Decide later
            </Button>
          )}
        </Box>
      );
    }
  }
};

// Render wizard form
const WizardForm = ({ wizard, colors, onFieldChange, onFieldBlur, onScriptValidate, onFormAction, onDeferField, onChangeAssumption, formValues = {}, isSubmitted = false, fieldErrors = {}, validatingFields = {} }) => {
  const [showOptional, setShowOptional] = useState(false);

  if (!wizard || !wizard.form) return null;

  const { form, progress, status, assumptions, confidenceCopy, collectedData } = wizard;
  const isCompleted = status === 'COMPLETED';
  const isCancelled = status === 'CANCELLED';
  const hasError = status === 'ERROR';

  if (isCompleted || isCancelled) {
    return null; // Don't render form for completed/cancelled wizards
  }

  const hasOptionalFields = form.optionalFields && form.optionalFields.length > 0;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderLeft: isSubmitted ? `4px solid ${colors.textMuted}` : `4px solid #22c55e`,
        opacity: isSubmitted ? 0.6 : 1,
      }}
    >
      <WizardProgress progress={progress} colors={colors} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {form.title}
        </Typography>
        {isSubmitted && (
          <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>
            SUBMITTED
          </Typography>
        )}
      </Box>

      {form.description && (
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
          {form.description}
        </Typography>
      )}

      {/* Confidence copy */}
      <ConfidenceCopy message={confidenceCopy} colors={colors} />

      {/* System assumptions strip */}
      <AssumptionsStrip
        assumptions={assumptions}
        colors={colors}
        onChangeAssumption={onChangeAssumption}
      />

      {hasError && (wizard.error || wizard.validation?.hasErrors) && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'rgba(239, 68, 68, 0.1)', borderLeft: '4px solid #ef4444' }}>
          <Typography variant="body2" sx={{ color: '#ef4444' }}>
            {wizard.error?.message || wizard.validation?.errors?.[0]?.message || 'An error occurred'}
          </Typography>
        </Box>
      )}

      {/* Required fields */}
      {form.fields?.map((field) => (
        <WizardField
          key={field.fieldId}
          field={field}
          colors={colors}
          onChange={onFieldChange}
          onBlur={onFieldBlur}
          onScriptValidate={onScriptValidate}
          onDefer={onDeferField}
          currentValue={formValues[field.fieldId]}
          disabled={isSubmitted}
          collectedData={collectedData}
          validationError={fieldErrors[field.fieldId]}
          isValidating={validatingFields[field.fieldId] || false}
        />
      ))}

      {/* Optional fields panel (collapsible) */}
      {hasOptionalFields && (
        <Box sx={{ mt: 2 }}>
          <Button
            size="small"
            onClick={() => setShowOptional(!showOptional)}
            endIcon={showOptional ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              color: colors.textSecondary,
              mb: 1,
              '&:hover': { bgcolor: 'transparent', color: colors.textPrimary },
            }}
          >
            {showOptional ? 'Hide options' : 'More options'} ({form.optionalFields.length})
          </Button>
          <Collapse in={showOptional}>
            <Box sx={{ pl: 1, borderLeft: `2px solid ${colors.borderLight}` }}>
              {form.optionalFields.map((field) => (
                <WizardField
                  key={field.fieldId}
                  field={field}
                  colors={colors}
                  onChange={onFieldChange}
                  onBlur={onFieldBlur}
                  onScriptValidate={onScriptValidate}
                  onDefer={onDeferField}
                  currentValue={formValues[field.fieldId]}
                  disabled={isSubmitted}
                  collectedData={collectedData}
                  validationError={fieldErrors[field.fieldId]}
                  isValidating={validatingFields[field.fieldId] || false}
                />
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Lines summary for multi-line wizards (receiving, etc.) */}
      {form.existingLines && form.existingLines.length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 600 }}>
            Items to Receive ({form.existingLines.length})
          </Typography>
          <Box sx={{
            bgcolor: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: 1,
            overflow: 'hidden'
          }}>
            {form.existingLines.map((line, index) => (
              <Box
                key={index}
                sx={{
                  p: 1.5,
                  borderBottom: index < form.existingLines.length - 1 ? `1px solid ${colors.border}` : 'none',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center'
                }}
              >
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>Item</Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                    {line.item_id || line.itemId || line.item_sku || '-'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 60 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>Qty</Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                    {line.quantity || line.expectedQuantity || '-'}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 60 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>UOM</Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                    {line.uom || 'EACH'}
                  </Typography>
                </Box>
                {(line.lpn || line.associatedLpn) && (
                <Box sx={{ minWidth: 150, flex: 1 }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>LPN</Typography>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {line.lpn || line.associatedLpn}
                  </Typography>
                </Box>
                )}
                {(line.lot_code || line.expectedLotCode) && (
                  <Box sx={{ minWidth: 80 }}>
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>Lot</Typography>
                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                      {line.lot_code || line.expectedLotCode}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {form.actions && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {form.actions.map((action) => {
            // Disable primary/submit actions if there are validation errors
            const hasValidationErrors = Object.keys(fieldErrors).length > 0;
            const shouldDisable = action.disabled || isSubmitted ||
              (hasValidationErrors && (action.style === 'PRIMARY' || action.type === 'SUBMIT'));
            return (
            <Button
              key={action.actionId}
              variant={action.style === 'PRIMARY' ? 'contained' : 'outlined'}
              size="small"
              disabled={shouldDisable}
              onClick={() => onFormAction(action)}
              sx={{
                ...(action.style === 'PRIMARY' && {
                  bgcolor: '#22c55e',
                  color: '#09090b',
                  '&:hover': { bgcolor: '#16a34a' },
                }),
                ...(action.style === 'DANGER' && {
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' },
                }),
                ...(action.style === 'SECONDARY' && {
                  borderColor: colors.border,
                  color: colors.textSecondary,
                }),
              }}
            >
              {action.label}
            </Button>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// A fenced code block whose content looks like a raw JSON payload (e.g. a
// tool-call result a reply is quoting as proof) starts collapsed behind a
// "Show details" toggle -- business users get the plain-language outcome
// sentence around it without a wall of JSON; anyone who wants the raw data
// can expand it. Real code snippets (no leading {/[ ) stay expanded as
// before -- only JSON-shaped blocks are treated as "technical detail".
const CollapsibleCodeBlock = ({ text, colors }) => {
  const looksLikeJson = /^[[{]/.test(text.trim());
  const [expanded, setExpanded] = useState(!looksLikeJson);

  return (
    <Box sx={{ my: 1 }}>
      {looksLikeJson && (
        <Button
          size="small"
          onClick={() => setExpanded((e) => !e)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ textTransform: 'none', color: colors.textSecondary, mb: expanded ? 0.5 : 0, px: 0.5, minWidth: 0 }}
        >
          {expanded ? 'Hide details' : 'Show details'}
        </Button>
      )}
      {expanded && (
        <Box
          component="pre"
          sx={{
            bgcolor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
            p: 1.5,
            overflow: 'auto',
            borderRadius: 0,
            m: 0,
          }}
        >
          <Typography
            component="code"
            sx={{
              color: colors.textPrimary,
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {text}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Main message renderer component
const MessageRenderer = ({
  message,
  colors,
  isDark,
  wizardFormValues = {},
  isWizardSubmitted = false,
  fieldErrors = {},
  validatingFields = {},
  onActionClick,
  onWizardFieldChange,
  onWizardFieldBlur,
  onWizardScriptValidate,
  onWizardAction,
  onTableRowAction,
  onTableBulkAction,
  onTableGlobalAction,
  onTableLinkClick,
  onTableConfigure,
  onTableFilterRemove,
  onTableAddFilter,
  onChartActionClick,
  onTimelineEventAction,
  onTimelineAction,
  onDiagnosticRuleAction,
  onDiagnosticAction,
  onRelatedEntityClick,
  onPinToBoard,
  onFileDownload,
  onFilePreview,
  onViewTableInWorkspace,
}) => {
  // Shared markdown components for consistent styling
  const markdownComponents = {
    p: ({ children }) => (
      <Typography variant="body2" sx={{ color: colors.textPrimary, mb: 1, lineHeight: 1.6 }}>
        {children}
      </Typography>
    ),
    strong: ({ children }) => (
      <Typography component="span" sx={{ fontWeight: 'bold', color: '#22c55e' }}>
        {children}
      </Typography>
    ),
    em: ({ children }) => (
      <Typography component="span" sx={{ fontStyle: 'italic', color: colors.textSecondary }}>
        {children}
      </Typography>
    ),
    h1: ({ children }) => (
      <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700, mt: 2, mb: 1 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 1.5, mb: 0.5 }}>
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 1, mb: 0.5 }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: 2.5, my: 1, color: colors.textPrimary }}>
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 2.5, my: 1, color: colors.textPrimary }}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <Typography component="li" variant="body2" sx={{ color: colors.textPrimary, mb: 0.5 }}>
        {children}
      </Typography>
    ),
    // react-markdown v8+ no longer passes an `inline` prop to the `code`
    // renderer (removed upstream) -- it was always `undefined` here, so
    // every code span, one word or a full JSON blob, fell into the block
    // branch and rendered as a full-width box, breaking up plain sentences.
    // Real fix (not a CSS patch): a genuine inline code span never contains
    // a raw newline in its text content; a fenced block almost always does.
    // Block-level JSON blocks route through CollapsibleCodeBlock so raw
    // tool-result payloads default to collapsed for business users. A long
    // JSON object quoted as a single-backtick span (no newline at all, so
    // CommonMark treats it as inline) is also routed to the block/collapsed
    // renderer -- it's genuinely a "raw payload," not a short inline term,
    // and left as inline text it would just wrap as one giant orange blob.
    code: ({ children }) => {
      const text = String(children).replace(/\n$/, '');
      const looksLikeLongJson = /^[[{]/.test(text.trim()) && text.length > 60;
      const isInline = !text.includes('\n') && !looksLikeLongJson;
      return isInline ? (
        <Typography
          component="code"
          sx={{
            bgcolor: colors.surfaceElevated,
            color: '#f59e0b',
            px: 0.5,
            py: 0.25,
            borderRadius: '2px',
            fontFamily: 'monospace',
            fontSize: '0.85em',
          }}
        >
          {text}
        </Typography>
      ) : (
        <CollapsibleCodeBlock text={text} colors={colors} />
      );
    },
    pre: ({ children }) => (
      <Box sx={{ my: 1 }}>
        {children}
      </Box>
    ),
    blockquote: ({ children }) => (
      <Box
        sx={{
          borderLeft: '4px solid #22c55e',
          pl: 2,
          py: 0.5,
          my: 1,
          bgcolor: 'rgba(34, 197, 94, 0.05)',
        }}
      >
        {children}
      </Box>
    ),
    a: ({ href, children }) => (
      <Typography
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: '#3b82f6',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
      >
        {children}
      </Typography>
    ),
    hr: () => (
      <Box
        component="hr"
        sx={{
          border: 'none',
          borderTop: `1px solid ${colors.border}`,
          my: 2,
        }}
      />
    ),
    table: ({ children }) => (
      <Box sx={{ overflowX: 'auto', my: 1 }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.875rem',
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    thead: ({ children }) => (
      <Box component="thead" sx={{ bgcolor: colors.surfaceElevated }}>
        {children}
      </Box>
    ),
    tbody: ({ children }) => (
      <Box component="tbody">{children}</Box>
    ),
    tr: ({ children }) => (
      <Box component="tr" sx={{ borderBottom: `1px solid ${colors.border}` }}>
        {children}
      </Box>
    ),
    th: ({ children }) => (
      <Box
        component="th"
        sx={{
          p: 1,
          textAlign: 'left',
          fontWeight: 600,
          color: colors.textPrimary,
          borderBottom: `2px solid ${colors.border}`,
        }}
      >
        {children}
      </Box>
    ),
    td: ({ children }) => (
      <Box
        component="td"
        sx={{
          p: 1,
          color: colors.textPrimary,
        }}
      >
        {children}
      </Box>
    ),
  };

  // If message doesn't have the new format (a RESTORED history message --
  // getSessionMessages only persists flattened text, never the structured
  // ChatResponse), render as plain text (backward compatibility) -- EXCEPT a
  // restored message can still contain the raw fenced ```json {"actions":
  // [...]}``` block verbatim in its text (a live message strips this out
  // via responseData.actions instead), which would otherwise show up as
  // literal, unrendered JSON to the tenant (real bug hit live 2026-09-23).
  // Parse it out here and render real buttons, same as the live ACTIONS path.
  if (!message.responseData) {
    const actionsBlockMatch = message.text &&
      message.text.match(/```json\s*(\{[\s\S]*?"actions"[\s\S]*?\})\s*```/);
    if (actionsBlockMatch) {
      try {
        const parsed = JSON.parse(actionsBlockMatch[1]);
        if (Array.isArray(parsed.actions)) {
          const bodyText = message.text.slice(0, actionsBlockMatch.index).trim();
          return (
            <Box>
              {bodyText && (
                <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={markdownComponents}>
                  {bodyText}
                </ReactMarkdown>
              )}
              <ActionButtons
                actions={parsed.actions}
                onActionClick={onActionClick}
                colors={colors}
              />
            </Box>
          );
        }
      } catch (e) {
        // Malformed block -- fall through to plain rendering below rather than crash.
      }
    }
    return (
      <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={markdownComponents}>
        {message.text}
      </ReactMarkdown>
    );
  }

  const responseData = message.responseData;
  const responseType = detectResponseType(responseData);

  // PLAIN response. A reply reopened from History carries only text + tables/charts (no rating), so
  // it would land here and lose its tables (FH-180) -- send it to the full renderer below instead.
  const hasBlocks = (responseData.tables?.length || 0) + (responseData.charts?.length || 0) > 0;
  if (responseType === 'PLAIN' && !hasBlocks) {
    return (
      <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={markdownComponents}>
        {responseData.response}
      </ReactMarkdown>
    );
  }

  // ACTIONS response (agent panel action buttons)
  if (responseType === 'ACTIONS') {
    // Real bug fixed 2026-09-22: this only checked responseData.reply, but
    // StructuredResponseParser (backend) puts prose-before-the-JSON-block into
    // .response, not .reply, whenever there's no table/chart/wizard alongside
    // the actions -- exactly the shape every "how does X work" agent answer
    // now uses. Result: the whole step-by-step explanation silently vanished,
    // leaving only bare action buttons with no context above them.
    const bodyText = responseData.reply || responseData.response;
    return (
      <Box>
        {bodyText && (
          <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={markdownComponents}>
            {bodyText}
          </ReactMarkdown>
        )}
        <ActionButtons
          actions={responseData.actions}
          onActionClick={onActionClick}
          colors={colors}
        />
      </Box>
    );
  }

  // TABLE, STRUCTURED, or WIZARD response
  const ratingColor = getRatingColor(responseData.rating);

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Rating badge and reply */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        {/* NORMAL is the everyday rating: a chip saying so on every table reply read as stray text */}
        {responseData.rating && responseData.rating !== 'NORMAL' && <RatingBadge rating={responseData.rating} />}
      </Box>

      {/* Main reply/summary text -- rendered via ReactMarkdown (same as the
          PLAIN response path above) so wizard copy (e.g. an ORIENTATION
          step's markdown-formatted overview) actually renders instead of
          showing literal "**"/newline escapes. A Box wrapper (not
          Typography) avoids nesting a <p> inside a <p>, since the "p"
          markdown component below already renders as a Typography/<p>. */}
      {(responseData.reply || responseData.response) && (
        <Box
          sx={{
            mb: 1,
            borderLeft: `3px solid ${ratingColor}`,
            pl: 1.5,
          }}
        >
          <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS} components={markdownComponents}>
            {responseData.reply || responseData.response}
          </ReactMarkdown>
        </Box>
      )}

      {/* Next move recommendation */}
      {responseData.nextMove && (
        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
          {responseData.nextMove}
        </Typography>
      )}

      {/* TABLE rendering -- as of 2026-09-28, chat no longer renders the grid
          inline (user's explicit call, after trying inline+pointer together:
          "I dont want inline anymore. just the pointer") -- only a link
          pointing at the real render in the "Atom Response" workspace tab.
          See project_atom_response_workspace_tab memory. Still loops over
          every table a turn produced (the real multi-table-per-turn bug fix
          from 2026-09-23 still applies -- one pointer per table). */}
      {(responseData.tables || (responseData.table ? [responseData.table] : [])).map((table, idx) => {
        // Stable id shared with AtomApp's atomResponseResults accumulator --
        // MUST be computed the same way in both places (message.id + index)
        // so this link can scroll-to-and-highlight the exact matching entry
        // in the "Atom Response" workspace tab, not just open the tab
        // generically. See project_atom_response_workspace_tab memory.
        const resultId = `${message.id}-${idx}`;
        const rowCount = Array.isArray(table?.rows) ? table.rows.length : null;
        return (
          <Box
            key={table?.title ? `${table.title}-${idx}` : idx}
            sx={{ mb: 1, p: 1, border: `1px solid ${colors.border}`, borderRadius: 1, bgcolor: colors.surface }}
          >
            <Button
              size="small"
              startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => onViewTableInWorkspace && onViewTableInWorkspace(resultId)}
              sx={{
                textTransform: 'none',
                color: colors.textPrimary,
                fontSize: '0.8rem',
                minWidth: 0,
                p: 0,
                '&:hover': { bgcolor: 'transparent', color: '#22c55e' },
              }}
            >
              {table?.title || 'View table'}{rowCount !== null ? ` (${rowCount})` : ''} — view in workspace
            </Button>
          </Box>
        );
      })}

      {/* CHART rendering -- same multi-block fix as TABLE above. */}
      {(responseData.charts || (responseData.chart ? [responseData.chart] : [])).map((chart, idx) => (
        <ChartRenderer
          key={chart?.title ? `${chart.title}-${idx}` : idx}
          chart={chart}
          colors={colors}
          onActionClick={onChartActionClick || onActionClick}
          onPinToBoard={onPinToBoard}
          sourceQuery={message.text || message.sourceQuery}
        />
      ))}

      {/* TIMELINE rendering */}
      {responseData.timeline && (
        <TimelineRenderer
          timeline={responseData.timeline}
          colors={colors}
          onEventAction={onTimelineEventAction || onActionClick}
          onTimelineAction={onTimelineAction || onActionClick}
          onRelatedEntityClick={onRelatedEntityClick}
          onPinToBoard={onPinToBoard}
          sourceQuery={message.text || message.sourceQuery}
        />
      )}

      {/* DIAGNOSTIC rendering */}
      {responseData.diagnostic && (
        <DiagnosticRenderer
          diagnostic={responseData.diagnostic}
          colors={colors}
          nextMove={responseData.nextMove}
          onRuleAction={onDiagnosticRuleAction || onActionClick}
          onDiagnosticAction={onDiagnosticAction || onActionClick}
          onRelatedEntityClick={onRelatedEntityClick}
          onPinToBoard={onPinToBoard}
          sourceQuery={message.text || message.sourceQuery}
        />
      )}

      {/* FILE_SEARCH rendering */}
      {responseData.fileSearchResults && (
        <FileSearchResults
          fileSearchResults={responseData.fileSearchResults}
          colors={colors}
          onDownload={onFileDownload}
          onPreview={onFilePreview}
        />
      )}

      {/* Wizard form if present */}
      {responseData.wizard && responseData.wizard.form && (
        <WizardForm
          wizard={responseData.wizard}
          colors={colors}
          formValues={wizardFormValues}
          isSubmitted={isWizardSubmitted}
          fieldErrors={fieldErrors}
          validatingFields={validatingFields}
          onFieldChange={onWizardFieldChange}
          onFieldBlur={onWizardFieldBlur}
          onScriptValidate={onWizardScriptValidate}
          onFormAction={onWizardAction}
          onDeferField={(fieldId) => onWizardAction && onWizardAction({ actionId: 'defer', fieldId })}
          onChangeAssumption={(fieldId, value) => onWizardFieldChange && onWizardFieldChange(fieldId, value)}
        />
      )}

      {/* Wizard completion summary */}
      {responseData.wizard && responseData.wizard.status === 'COMPLETED' && responseData.wizard.result && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid #22c55e',
          }}
        >
          <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 500 }}>
            {responseData.wizard.result.createdEntity?.type} {responseData.wizard.result.createdEntity?.id} created successfully
          </Typography>
        </Box>
      )}

      {/* Post-wizard next actions */}
      {responseData.wizard && responseData.wizard.status === 'COMPLETED' && responseData.wizard.suggestedNextActions && (
        <PostWizardActions
          nextActions={responseData.wizard.suggestedNextActions}
          colors={colors}
          onActionClick={onActionClick}
        />
      )}

      {/* Action buttons */}
      <ActionButtons
        actions={responseData.actions}
        onActionClick={onActionClick}
        colors={colors}
      />
    </Box>
  );
};

export default MessageRenderer;
